import { EventProperties } from "@segment/analytics-next";

import { getOscConfig } from "@twiliointernal/e2-commons-shared-definitions";

import { types } from "./definitions";
import logger from "../logger";

const { segmentWriteKey } = getOscConfig();

class Segment {
  analytics: types.Analytics | undefined;
  private isUserIdentified: boolean;
  private globalEventAttributes: EventProperties;

  constructor() {
    this.isUserIdentified = false;
    this.globalEventAttributes = {
      appName: "one-support-center",
    };
    this._initAnalytics();
    this._initConsentManager();
  }

  // Source: https://docs.google.com/document/d/1-xSd988839qM3NyJoBlc1KPs2SsbugZOf84VGttNvho/edit?usp=sharing
  _initAnalytics = (): void => {
    const analytics: types.Analytics = (window.analytics =
      window.analytics || []);

    if (!analytics.initialize) {
      if (analytics.invoked) {
        logger.error("Segment snippet included twice");
      } else {
        analytics.invoked = !0;
        analytics.methods = [
          "trackSubmit",
          "trackClick",
          "trackLink",
          "trackForm",
          "pageview",
          "identify",
          "reset",
          "group",
          "track",
          "ready",
          "alias",
          "debug",
          "page",
          "once",
          "off",
          "on",
          "addSourceMiddleware",
          "addIntegrationMiddleware",
          "setAnonymousId",
          "addDestinationMiddleware",
        ];
        analytics.factory = function (e: string, ...args) {
          return function () {
            const t = Array.prototype.slice.call(args);
            t.unshift(e);
            analytics.push(t);
            return analytics;
          };
        };
        for (let index = 0; index < analytics.methods.length; index++) {
          const key: string = analytics.methods[index];
          analytics[key] = analytics.factory(key);
        }
        analytics.load = function (key: string, e): void {
          const t = document.createElement("script");
          t.type = "text/javascript";
          t.async = !0;
          t.src =
            "https://cdn.segment.com/analytics.js/v1/" +
            key +
            "/analytics.min.js";
          const n = document.getElementsByTagName("script")[0];
          if (n.parentNode) {
            n.parentNode.insertBefore(t, n);
          }
          analytics._loadOptions = e;
        };
        analytics._writeKey = segmentWriteKey;
        analytics.SNIPPET_VERSION = "4.16.1";
      }
    }
  };

  // Source: https://docs.google.com/document/d/1-xSd988839qM3NyJoBlc1KPs2SsbugZOf84VGttNvho/edit?usp=sharing
  _initConsentManager = (): void => {
    const OPT_IN = true;
    const WEBSITE_WRITE_KEY = segmentWriteKey;

    const miCookieValor = getCookieValue("cmapi_cookie_privacy");

    // gets enabled destination configurations from across your source (and optionally other sources in workspace)
    fetchDestinations([WEBSITE_WRITE_KEY]).then((destinations) => {
      const consentDecision = miCookieValor;
      const destinationPreferences = destinations
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map(function (
          dest: types.Destination
        ): Record<string, boolean> | undefined {
          if (!dest.id) {
            return undefined;
          }

          if (consentDecision === "permit 1,2,3") return { [dest.id]: true };
          if (consentDecision === "permit 1,2 functional")
            return {
              [dest.id]: dest.category === "Advertising" ? false : true,
            };
          if (consentDecision === "permit 1,3")
            return {
              [dest.id]: dest.category === "Advertising" ? true : false,
            };
          if (consentDecision === "permit 1 required")
            return { [dest.id]: false };
        })
        .reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (acc: Record<string, any>, val) => {
            return {
              ...val,
              ...acc,
            };
          },
          { "Segment.io": consentDecision.includes("2") ? true : false }
        );
      conditionallyLoadAnalytics({
        writeKey: WEBSITE_WRITE_KEY,
        destinationPreferences,
        isConsentRequired: OPT_IN,
      });
    });
    // Load Cookie Preferences
    function getCookieValue(cookieName: string): string {
      const name = cookieName + "=";
      const decodedCookie = decodeURIComponent(document.cookie);
      const cookieArray = decodedCookie.split(";");
      for (let i = 0; i < cookieArray.length; i++) {
        let cookie = cookieArray[i];
        while (cookie.charAt(0) === " ") {
          cookie = cookie.substring(1);
        }
        if (cookie.indexOf(name) === 0) {
          return cookie.substring(name.length, cookie.length);
        }
      }
      return "permit 1 required";
    }
    // Load Integrations
    function conditionallyLoadAnalytics({
      writeKey,
      destinationPreferences,
      isConsentRequired,
    }: types.ConditionallyLoadAnalyticsArgs): void {
      let isAnythingEnabled = false;
      if (!destinationPreferences) {
        if (isConsentRequired) {
          return;
        }
        // Load a.js normally when consent isn’t required and there’s no preferences
        if (!window.analytics.initialized) {
          window.analytics.load(writeKey);
        }
        return;
      }
      for (const destination of Object.keys(destinationPreferences)) {
        const isEnabled = destinationPreferences[destination];
        if (isEnabled) {
          isAnythingEnabled = true;
        }
      }
      // Don’t load a.js at all if nothing has been enabled
      if (isAnythingEnabled) {
        window.analytics.load(writeKey, {
          integrations: destinationPreferences,
        });
      }
    }
    async function fetchDestinationForWriteKey(
      writeKey: string
    ): Promise<types.Destination[]> {
      const res = await window.fetch(
        "https://cdn.segment.com/v1/projects/" + writeKey + "/integrations"
      );
      if (!res.ok) {
        logger.warn("Failed to fetch Segment integrations");
        throw new Error(
          "Failed to fetch integrations for write key " +
            writeKey +
            ": HTTP+" +
            res.status +
            res.statusText
        );
      }
      const destinations = await res.json();
      // Rename creationName to id to abstract the weird data model
      for (const destination of destinations) {
        destination.id = destination.creationName;
        delete destination.creationName;
      }
      return destinations;
    }
    async function fetchDestinations(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...writeKeys: any[]
    ): Promise<types.Destination[]> {
      const destinationsRequests: Promise<types.Destination[]>[] = [];
      for (const writeKey of writeKeys) {
        const request = fetchDestinationForWriteKey(writeKey);
        destinationsRequests.push(request);
      }
      const destinations = await Promise.all(destinationsRequests);
      const flatDestinations: types.Destination[] = [
        ...destinations
          .reduce((a, b) => a.concat(b), []) // flatten multi-d array
          .filter((d) => d.id !== "Repeater") // remove repeater
          .reduce((map, item) => {
            map.has(item["id"]) || map.set(item["id"], item);
            return map;
          }, new Map())
          .values(),
      ];
      return flatDestinations;
    }
  };

  addGlobalEventAttribute(newEventAttribute: EventProperties): void {
    this.globalEventAttributes = {
      ...this.globalEventAttributes,
      ...newEventAttribute,
    };
  }

  /**
   * This solution is a "best effort" to track ungated users. We previously relied on Segment's
   * anonymous user ID, but found that in our deployed environments it wasn't persistent cross-session.
   *
   * DD session ID has been found to be persistent, and will be what we use moving forward.
   */
  getAnonymousUserId(): string {
    return logger.getInternalContext().session_id;
  }

  identifyUser({ userSid, accountSid }: types.SegmentIdentifyUserParams): void {
    if (this.isUserIdentified) {
      return;
    }

    if (window.analytics) {
      if (userSid && accountSid) {
        // For TW Customers
        window.analytics.identify(userSid);
        window.analytics.group(accountSid);
      } else {
        // Ungated users
        const anonymousUserId = this.getAnonymousUserId();

        window.analytics.identify(anonymousUserId);
      }
      this.isUserIdentified = true;
    }
  }

  trackEvent(eventName: string, additionalValues?: EventProperties): void {
    if (window.analytics) {
      window.analytics.track(eventName, {
        ...this.globalEventAttributes,
        ...additionalValues,
      });
    }
  }

  trackPage(): void {
    if (window.analytics) {
      window.analytics.page();
    }
  }
}

export default new Segment();
